import { OperationLog } from "@/domain/entity/admin/operation-log-entity";
import { AddTreatmentRequestPayload } from "@/data/payload/treatment/add-treatment-request-payload";
import { UserRole } from "@/domain/entity/auth/user-session-entity";
import {
  DashboardData,
  DashboardHomeScope,
} from "@/domain/entity/dashboard/dashboard-entity";
import { MedicalExpert } from "@/domain/entity/doctor/medical-expert-entity";
import { HospitalInfoEntity } from "@/domain/entity/hospital-info-entity";
import {
  LOW_COMPLIANCE_THRESHOLD,
  PatientDetail,
  PatientListFilter,
  PatientSummary,
} from "@/domain/entity/patient/patient-info-entity";
import {
  ActivityDetail,
  EyeActivityDataModel,
  PrescriptionDetail,
  PrescriptionSummary,
} from "@/domain/entity/prescription/prescription-info-entity";
import { UserInfoEntity } from "@/domain/entity/user/user-info-entity";

const MOCK_SESSION_KEY = "sat014-mock-session";

type MockAccount = {
  loginId: string;
  name: string;
  role: UserRole.Admin | UserRole.SuperAdmin | UserRole.Doctor | UserRole.Nurse | UserRole.Patient | UserRole.Cro;
  isExpiredSn: boolean;
  hospitalName?: string;
  hospitalCode?: string;
};

const defaultAccounts: MockAccount[] = [
  {
    loginId: "admin@salphadtx.com",
    name: "SAT-014 관리자",
    role: UserRole.SuperAdmin,
    isExpiredSn: false,
  },
  {
    loginId: "doctor@salphadtx.com",
    name: "김하늘",
    role: UserRole.Doctor,
    isExpiredSn: false,
    hospitalName: "서울대병원",
    hospitalCode: "SNU",
  },
  {
    loginId: "nurse@salphadtx.com",
    name: "박민지",
    role: UserRole.Nurse,
    isExpiredSn: false,
    hospitalName: "서울대병원",
    hospitalCode: "SNU",
  },
  {
    loginId: "cro@salphadtx.com",
    name: "최도윤",
    role: UserRole.Cro,
    isExpiredSn: false,
  },
  {
    loginId: "patient@salphadtx.com",
    name: "환자 가이드",
    role: UserRole.Patient,
    isExpiredSn: false,
  },
];

const mockPatientNames = [
  "정서윤",
  "김민준",
  "이하린",
  "박도현",
  "최지우",
  "한서준",
  "윤하은",
  "오지훈",
  "강민서",
  "서도윤",
  "임수아",
  "장현우",
  "송예린",
  "조민재",
  "문채원",
  "배준호",
  "신아린",
  "유시우",
  "권나윤",
  "남태준",
];

const basePatients: PatientDetail[] = [
  {
    doctorId: 1,
    doctorName: "김하늘",
    uuid: "patient-001",
    timeZone: "Asia/Seoul",
    progressStatus: "진행중",
    patientEmail: "patient001@example.com",
    patientName: mockPatientNames[0],
    isDeactivated: false,
    registrationDate: "2026-05-01",
    isTestAccount: true,
    screeningId: "SAT014-M001",
    phone: "+82 10-1234-0001",
    countryCode: "KR",
    gender: "female",
    birthday: "1988-03-12",
    memo: "순응도 양호. 2주차 과제 진행 중.",
  },
  {
    doctorId: 1,
    doctorName: "김하늘",
    uuid: "patient-002",
    timeZone: "Asia/Seoul",
    progressStatus: "시작 전(치료 완료)",
    patientEmail: "patient002@example.com",
    patientName: mockPatientNames[1],
    isDeactivated: false,
    registrationDate: "2026-05-03",
    isTestAccount: false,
    screeningId: "SAT014-M002",
    phone: "+82 10-1234-0002",
    countryCode: "KR",
    gender: "male",
    birthday: "1979-11-02",
    memo: "최근 활동 누락. 알림 발송 필요.",
  },
  {
    doctorId: 2,
    doctorName: "이서준",
    uuid: "patient-003",
    timeZone: "Asia/Seoul",
    progressStatus: "활동 완료(치료 종료)",
    patientEmail: "patient003@example.com",
    patientName: mockPatientNames[2],
    isDeactivated: true,
    registrationDate: "2026-04-18",
    isTestAccount: false,
    screeningId: "SAT014-M003",
    phone: "+82 10-1234-0003",
    countryCode: "KR",
    gender: "unknown",
    birthday: "1992-08-21",
    memo: "치료 종료 후 비활성화 상태.",
  },
  {
    doctorId: 2,
    doctorName: "이서준",
    uuid: "patient-004",
    timeZone: "Asia/Seoul",
    progressStatus: "비활성 계정",
    patientEmail: "patient004@example.com",
    patientName: mockPatientNames[3],
    isDeactivated: true,
    registrationDate: "2026-04-20",
    isTestAccount: false,
    screeningId: "SAT014-M004",
    phone: "+82 10-1234-0004",
    countryCode: "KR",
    gender: "unknown",
    birthday: "1989-06-09",
    memo: "비활성화된 목업 계정.",
  },
];

type PatientFixture = {
  progressStatus: string;
  prescriptionStatus?: string;
  complianceRate?: number;
  progressRate?: number;
  performanceRate?: number;
  isDeactivated?: boolean;
};

const patientFixtures: PatientFixture[] = [
  { progressStatus: "진행중", prescriptionStatus: "inProgress", complianceRate: 58, progressRate: 0.31, performanceRate: 0.18 },
  { progressStatus: "진행중", prescriptionStatus: "inProgress", complianceRate: 84, progressRate: 0.52, performanceRate: 0.81 },
  { progressStatus: "시작 전(치료 완료)", prescriptionStatus: "notStarted", complianceRate: 0, progressRate: 0, performanceRate: 0 },
  { progressStatus: "치료 없음" },
  { progressStatus: "진행중", prescriptionStatus: "warning", complianceRate: 37, progressRate: 0.45, performanceRate: 0.16 },
  { progressStatus: "활동 완료(치료 종료)", prescriptionStatus: "completed", complianceRate: 91, progressRate: 1, performanceRate: 0.94 },
  { progressStatus: "비활성 계정", prescriptionStatus: "canceled", complianceRate: 22, progressRate: 0.2, performanceRate: 0.08, isDeactivated: true },
  { progressStatus: "시작 전(치료 완료)", prescriptionStatus: "notStarted", complianceRate: 0, progressRate: 0, performanceRate: 0 },
  { progressStatus: "진행중", prescriptionStatus: "inProgress", complianceRate: 64, progressRate: 0.67, performanceRate: 0.43 },
  { progressStatus: "진행중", prescriptionStatus: "warning", complianceRate: 49, progressRate: 0.58, performanceRate: 0.27 },
  { progressStatus: "치료 없음" },
  { progressStatus: "활동 완료(치료 종료)", prescriptionStatus: "completed", complianceRate: 76, progressRate: 1, performanceRate: 0.76 },
  { progressStatus: "진행중", prescriptionStatus: "inProgress", complianceRate: 55, progressRate: 0.25, performanceRate: 0.14 },
  { progressStatus: "비활성 계정", isDeactivated: true },
  { progressStatus: "시작 전(치료 완료)", prescriptionStatus: "notStarted", complianceRate: 0, progressRate: 0, performanceRate: 0 },
  { progressStatus: "진행중", prescriptionStatus: "inProgress", complianceRate: 88, progressRate: 0.74, performanceRate: 0.65 },
];

const additionalPatients: PatientDetail[] = patientFixtures.map((fixture, index) => {
  const sequence = index + 5;
  const doctorIndex = index % 2;
  const doctor = doctorIndex === 0 ? { id: 1, name: "김하늘" } : { id: 2, name: "이서준" };
  return {
    doctorId: doctor.id,
    doctorName: doctor.name,
    uuid: `patient-${String(sequence).padStart(3, "0")}`,
    timeZone: index % 4 === 0 ? "America/Los_Angeles" : "Asia/Seoul",
    progressStatus: fixture.progressStatus,
    patientEmail: `patient${String(sequence).padStart(3, "0")}@example.com`,
    patientName: mockPatientNames[sequence - 1] ?? `목업환자${String(sequence).padStart(3, "0")}`,
    isDeactivated: fixture.isDeactivated ?? false,
    registrationDate: `2026-05-${String(Math.min(28, sequence)).padStart(2, "0")}`,
    isTestAccount: index % 5 === 0,
    screeningId: `SAT014-M${String(sequence).padStart(3, "0")}`,
    phone: `+82 10-1234-${String(sequence).padStart(4, "0")}`,
    countryCode: "KR",
    gender: index % 3 === 0 ? "female" : index % 3 === 1 ? "male" : "unknown",
    birthday: `${1978 + (index % 20)}-${String((index % 12) + 1).padStart(2, "0")}-${String((index % 27) + 1).padStart(2, "0")}`,
    memo: fixture.isDeactivated
      ? "비활성화된 목업 계정입니다."
      : fixture.progressStatus === "치료 없음"
        ? "아직 치료가 생성되지 않은 환자입니다."
        : (fixture.complianceRate ?? 0) < LOW_COMPLIANCE_THRESHOLD
          ? "순응도 관리가 필요한 환자입니다."
          : "정기 추적 중인 목업 환자입니다.",
  };
});

const patients: PatientDetail[] = [...basePatients, ...additionalPatients];

const weeklyPerformance = Array.from({ length: 6 }, (_, weekIndex) => {
  const activityNames = ["breath", "cloud", "eyeActivity", "nature", "energy", "core", "walk", "deepBreath"];
  const week = weekIndex + 1;
  return {
    week,
    compliance: Math.min(0.35 + week * 0.08, 0.88),
    days: Array.from({ length: 7 }, (__, dayIndex) => {
      const name = activityNames[(weekIndex + dayIndex) % activityNames.length];
      return {
        day: dayIndex + 1,
        type: activityTypeForName(name),
        name,
        progress: Math.min(0.2 + (week + dayIndex) * 0.08, 1),
        totalTime: 420 + dayIndex * 60,
      };
    }),
  };
});

function activityTypeForName(name: string): "internal" | "external" | "otherInternal" | "otherExternal" {
  switch (name) {
    case "breath":
    case "cloud":
    case "deepBreath":
      return "internal";
    case "eyeActivity":
    case "nature":
      return "external";
    case "energy":
    case "core":
      return "otherInternal";
    case "walk":
      return "otherExternal";
    default:
      return "internal";
  }
}

const prescriptionsByPatient: Record<string, PrescriptionDetail[]> = {
  "patient-001": [
    {
      patientUuid: "patient-001",
      prescriptionUuid: "prescription-001",
      prescriptionId: "RX-014-0001",
      prescriptionStatus: "inProgress",
      prescriptionType: "6",
      startDate: "2026-05-01",
      endDate: "2026-06-12",
      complianceRate: 72,
      progressRate: 0.43,
      performanceRate: 0.68,
      doctorId: 1,
      doctorName: "김하늘",
      weeklyPerformance,
      memo: "SAT-014 앱 전체 활동 데이터 샘플",
      plans: [
        {
          planUuid: "activity-001",
          title: "숨 고르기",
          day: 1,
          status: "완료",
          activityCount: 1,
          type: "internal",
          name: "breath",
          startAt: "2026-05-01T09:00:00+09:00",
          endAt: "2026-05-01T09:10:00+09:00",
          totalTime: 600,
        },
        {
          planUuid: "activity-003",
          title: "맑은 하늘 만들기",
          day: 1,
          status: "완료",
          activityCount: 1,
          type: "internal",
          name: "cloud",
          startAt: "2026-05-01T21:05:00+09:00",
          endAt: "2026-05-01T21:13:00+09:00",
          totalTime: 480,
        },
        {
          planUuid: "activity-006",
          title: "땅 다지기",
          day: 2,
          status: "완료",
          activityCount: 1,
          type: "internal",
          name: "core",
          startAt: "2026-05-02T07:40:00+09:00",
          endAt: "2026-05-02T07:52:00+09:00",
          totalTime: 720,
        },
        {
          planUuid: "activity-002",
          title: "산책하기",
          day: 2,
          status: "진행중",
          activityCount: 1,
          type: "external",
          name: "walk",
          startAt: "2026-05-02T18:10:00+09:00",
          endAt: "2026-05-02T18:18:00+09:00",
          totalTime: 480,
        },
        {
          planUuid: "activity-004",
          title: "자연 감상",
          day: 3,
          status: "완료",
          activityCount: 1,
          type: "external",
          name: "nature",
          startAt: "2026-05-03T08:10:00+09:00",
          endAt: "2026-05-03T08:18:00+09:00",
          totalTime: 480,
        },
        {
          planUuid: "activity-005",
          title: "활기 더하기",
          day: 3,
          status: "완료",
          activityCount: 1,
          type: "internal",
          name: "energy",
          startAt: "2026-05-03T20:30:00+09:00",
          endAt: "2026-05-03T20:42:00+09:00",
          totalTime: 720,
        },
        {
          planUuid: "activity-007",
          title: "눈 움직이기",
          day: 4,
          status: "완료",
          activityCount: 1,
          type: "external",
          name: "eyeActivity",
          startAt: "2026-05-04T11:00:00+09:00",
          endAt: "2026-05-04T11:16:00+09:00",
          totalTime: 960,
        },
        {
          planUuid: "activity-008",
          title: "심호흡",
          day: 4,
          status: "완료",
          activityCount: 1,
          type: "internal",
          name: "deepBreath",
          startAt: "2026-05-04T22:00:00+09:00",
          endAt: "2026-05-04T22:08:00+09:00",
          totalTime: 480,
        },
      ],
      activities: [
        { activityUuid: "activity-001", title: "breath", type: "internal", status: "completed" },
        { activityUuid: "activity-003", title: "cloud", type: "internal", status: "completed" },
        { activityUuid: "activity-006", title: "core", type: "internal", status: "completed" },
        { activityUuid: "activity-002", title: "walk", type: "external", status: "pending" },
        { activityUuid: "activity-004", title: "nature", type: "external", status: "completed" },
        { activityUuid: "activity-005", title: "energy", type: "internal", status: "completed" },
        { activityUuid: "activity-007", title: "eyeActivity", type: "external", status: "completed" },
        { activityUuid: "activity-008", title: "deepBreath", type: "internal", status: "completed" },
      ],
    },
  ],
  "patient-002": [
    {
      patientUuid: "patient-002",
      prescriptionUuid: "prescription-002",
      prescriptionId: "RX-014-0002",
      prescriptionStatus: "warning",
      prescriptionType: "6",
      startDate: "2026-05-03",
      endDate: "2026-06-14",
      complianceRate: 42,
      progressRate: 0.12,
      performanceRate: 0.05,
      doctorId: 1,
      doctorName: "김하늘",
      weeklyPerformance,
      memo: "순응도 저하",
      plans: [
        {
          planUuid: "activity-003",
          title: "맑은 하늘 만들기",
          day: 1,
          status: "미완료",
          activityCount: 1,
          type: "internal",
          name: "cloud",
          startAt: "2026-05-03T21:05:00+09:00",
          endAt: "2026-05-03T21:13:00+09:00",
          totalTime: 480,
        },
        {
          planUuid: "activity-005",
          title: "활기 더하기",
          day: 2,
          status: "대기",
          activityCount: 1,
          type: "otherInternal",
          name: "energy",
          startAt: "2026-05-04T20:30:00+09:00",
          endAt: null,
          totalTime: 0,
        },
      ],
      activities: [{ activityUuid: "activity-003", title: "cloud", type: "internal", status: "missed" }],
    },
  ],
  "patient-003": [
    {
      patientUuid: "patient-003",
      prescriptionUuid: "prescription-003",
      prescriptionId: "RX-014-0003",
      prescriptionStatus: "completed",
      prescriptionType: "6",
      startDate: "2026-04-18",
      endDate: "2026-05-30",
      complianceRate: 96,
      progressRate: 1,
      performanceRate: 0.96,
      doctorId: 2,
      doctorName: "이서준",
      weeklyPerformance,
      memo: "종료 치료",
      plans: [
        {
          planUuid: "activity-004",
          title: "자연 감상",
          day: 42,
          status: "완료",
          activityCount: 1,
          type: "external",
          name: "nature",
          startAt: "2026-05-25T08:10:00+09:00",
          endAt: "2026-05-25T08:18:00+09:00",
          totalTime: 480,
        },
      ],
      activities: [{ activityUuid: "activity-004", title: "nature", type: "external", status: "completed" }],
    },
  ],
};

const generatedPrescriptionsByPatient = additionalPatients.reduce<Record<string, PrescriptionDetail[]>>(
  (acc, patient, index) => {
    const fixture = patientFixtures[index];
    if (!fixture.prescriptionStatus) return acc;

    const sequence = index + 5;
    const startDay = Math.min(25, sequence);
    const startDate = `2026-05-${String(startDay).padStart(2, "0")}`;
    const endDate = `2026-06-${String(Math.min(28, startDay + 13)).padStart(2, "0")}`;

    acc[patient.uuid] = [
      {
        patientUuid: patient.uuid,
        prescriptionUuid: `prescription-${String(sequence).padStart(3, "0")}`,
        prescriptionId: `RX-014-${String(sequence).padStart(4, "0")}`,
        prescriptionStatus: fixture.prescriptionStatus,
        prescriptionType: "6",
        startDate,
        endDate,
        complianceRate: fixture.complianceRate ?? 0,
        progressRate: fixture.progressRate ?? 0,
        performanceRate: fixture.performanceRate ?? 0,
        doctorId: patient.doctorId ?? undefined,
        doctorName: patient.doctorName ?? undefined,
        weeklyPerformance,
        memo:
          fixture.prescriptionStatus === "warning"
            ? "순응도 저하로 추적이 필요한 목업 치료입니다."
            : fixture.prescriptionStatus === "canceled"
              ? "비활성화 전 취소된 목업 치료입니다."
              : "목업 환자 치료 샘플입니다.",
        plans: [
          {
            planUuid: sequence % 2 === 0 ? "activity-003" : "activity-001",
            title: sequence % 2 === 0 ? "맑은 하늘 만들기" : "숨 고르기",
            day: Math.max(1, index + 1),
            status: fixture.prescriptionStatus === "notStarted" ? "대기" : fixture.prescriptionStatus === "completed" ? "완료" : "진행중",
            activityCount: 1,
            type: sequence % 2 === 0 ? "internal" : "external",
            name: sequence % 2 === 0 ? "cloud" : "breath",
            startAt: `${startDate}T09:00:00+09:00`,
            endAt: fixture.prescriptionStatus === "notStarted" ? null : `${startDate}T09:10:00+09:00`,
            totalTime: fixture.prescriptionStatus === "notStarted" ? 0 : 600,
          },
        ],
        activities: [
          {
            activityUuid: sequence % 2 === 0 ? "activity-003" : "activity-001",
            title: sequence % 2 === 0 ? "cloud" : "breath",
            type: sequence % 2 === 0 ? "internal" : "external",
            status: fixture.prescriptionStatus === "completed" ? "completed" : "pending",
          },
        ],
      },
    ];

    return acc;
  },
  {},
);

Object.assign(prescriptionsByPatient, generatedPrescriptionsByPatient);

function latestPrescription(patientUuid: string): PrescriptionDetail | null {
  return prescriptionsByPatient[patientUuid]?.[0] ?? null;
}

function latestComplianceRate(patientUuid: string): number | null {
  return latestPrescription(patientUuid)?.complianceRate ?? null;
}

function patientSummary(patient: PatientDetail): PatientSummary {
  return {
    ...patient,
    latestComplianceRate: latestComplianceRate(patient.uuid),
  };
}

function isInProgressStatus(status?: string | null): boolean {
  const normalized = (status ?? "").toLowerCase();
  return normalized === "inprogress" || status === "진행중";
}

function isScheduledStatus(status?: string | null): boolean {
  const normalized = (status ?? "").toLowerCase();
  return normalized === "scheduled" || normalized === "notstarted" || status === "시작 전" || status === "시작 전(치료 완료)";
}

function isLowCompliancePatient(patientUuid: string, threshold = LOW_COMPLIANCE_THRESHOLD): boolean {
  const rate = latestComplianceRate(patientUuid);
  return rate !== null && rate > 0 && rate < threshold;
}

function matchesPatientFilter(patient: PatientDetail, filter?: PatientListFilter): boolean {
  if (!filter) return true;

  const hasListFilter = Boolean(filter.inProgress || filter.scheduled || filter.lowCompliance);
  const matchesListFilter =
    !hasListFilter ||
    (filter.inProgress && isInProgressStatus(patient.progressStatus)) ||
    (filter.scheduled && isScheduledStatus(patient.progressStatus)) ||
    (filter.lowCompliance && isLowCompliancePatient(patient.uuid, filter.lowComplianceThreshold));

  const matchesActiveFilter = !filter.activeOnly || !patient.isDeactivated;
  return Boolean(matchesListFilter && matchesActiveFilter);
}

function makeHeartRateData(
  _startAt: string,
  values: number[],
): Array<{ heartRate: number; time: string }> {
  return values.map((heartRate, index) => ({
    heartRate,
    time: String(index * 60),
  }));
}

const activityDetails: Record<string, ActivityDetail> = {
  "activity-001": {
    breathingList: [
      {
        startTime: "2026-05-01T09:04:00+09:00",
        endTime: "2026-05-01T09:05:00+09:00",
      },
    ],
    heartRateInfo: {
      highestHeartRate: 126,
      lowestHeartRate: 74,
      averageHeartRate: 96,
      hrData: makeHeartRateData("2026-05-01T09:00:00+09:00", [
        74, 82, 93, 101, 126, 119, 104, 91, 84, 80,
      ]),
    },
    discomfortInfo: [
      { id: 1, activityId: 1, when: "before", score: 7 },
      { id: 2, activityId: 1, when: "after", score: 3 },
    ],
    cloudInfo: [],
    coreInfo: [
      {
        index: 0,
        detailData: {
          choices: [
            { title: "복식 호흡", isUse: true, rate: 5 },
            { title: "긴장 이완", isUse: true, rate: 4 },
          ],
          selectedChoices: [{ title: "복식 호흡", isUse: true, rate: 5 }],
        },
      },
    ],
    energyInfo: [],
    walkInfo: [],
    feedbacks: [{ comment: "심박 상승 후 회복 속도가 양호합니다. 같은 패턴으로 유지해 주세요.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
  "activity-002": {
    breathingList: [],
    heartRateInfo: {
      highestHeartRate: 132,
      lowestHeartRate: 88,
      averageHeartRate: 111,
      hrData: makeHeartRateData("2026-05-02T18:10:00+09:00", [
        88, 96, 105, 112, 120, 132, 124, 116,
      ]),
    },
    discomfortInfo: [
      { id: 3, activityId: 2, when: "before", score: 5 },
      { id: 4, activityId: 2, when: "after", score: 4 },
    ],
    cloudInfo: [],
    coreInfo: [],
    energyInfo: [],
    walkInfo: [{ index: 0, detailData: { result: 1264 } }],
    feedbacks: [],
  },
  "activity-003": {
    breathingList: [
      {
        startTime: "2026-05-03T21:09:00+09:00",
        endTime: "2026-05-03T21:10:00+09:00",
      },
    ],
    heartRateInfo: {
      highestHeartRate: 118,
      lowestHeartRate: 76,
      averageHeartRate: 92,
      hrData: makeHeartRateData("2026-05-03T21:05:00+09:00", [
        76, 84, 91, 108, 118, 101, 92, 86,
      ]),
    },
    discomfortInfo: [{ id: 5, activityId: 3, when: "before", score: 8 }],
    cloudInfo: [
      {
        index: 0,
        detailData: {
          choices: [
            { id: "thought-1", title: "업무 생각" },
            { id: "thought-2", title: "수면 걱정" },
          ],
          cognitiveRestructuring: [
            {
              cognitiveThinkingId: "수면 걱정",
              restructuredThinkingId: "오늘은 짧게라도 회복 시간을 확보한다",
            },
          ],
        },
      },
    ],
    coreInfo: [],
    energyInfo: [],
    walkInfo: [],
    feedbacks: [{ comment: "활동 후 불편감 입력이 없어 다음 방문 때 확인이 필요합니다.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
  "activity-004": {
    breathingList: [],
    heartRateInfo: {
      highestHeartRate: 102,
      lowestHeartRate: 70,
      averageHeartRate: 84,
      hrData: makeHeartRateData("2026-05-25T08:10:00+09:00", [
        70, 76, 82, 88, 95, 102, 92, 83,
      ]),
    },
    discomfortInfo: [
      { id: 6, activityId: 4, when: "before", score: 3 },
      { id: 7, activityId: 4, when: "after", score: 1 },
    ],
    cloudInfo: [],
    coreInfo: [],
    energyInfo: [],
    walkInfo: [],
    feedbacks: [{ comment: "치료 종료 전 마지막 활동이 안정적으로 수행되었습니다.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
  "activity-005": {
    breathingList: [],
    heartRateInfo: {
      highestHeartRate: 114,
      lowestHeartRate: 72,
      averageHeartRate: 91,
      hrData: makeHeartRateData("2026-05-03T20:30:00+09:00", [
        72, 78, 84, 92, 101, 114, 105, 96, 88, 82, 79, 75,
      ]),
    },
    discomfortInfo: [
      { id: 8, activityId: 5, when: "before", score: 6 },
      { id: 9, activityId: 5, when: "after", score: 3 },
    ],
    cloudInfo: [],
    coreInfo: [],
    energyInfo: [
      {
        index: 0,
        detailData: {
          choices: [
            { title: "가벼운 스트레칭", isUse: true, rate: 3 },
            { title: "음악 듣기", isUse: true, rate: 4 },
          ],
          selectedChoices: [{ title: "음악 듣기", isUse: true, rate: 4 }],
        },
      },
    ],
    walkInfo: [],
    feedbacks: [{ comment: "활기 더하기 활동에서 선호 활동 선택과 심박 회복 패턴이 모두 확인됩니다.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
  "activity-006": {
    breathingList: [
      {
        startTime: "2026-05-02T07:47:00+09:00",
        endTime: "2026-05-02T07:48:30+09:00",
      },
    ],
    heartRateInfo: {
      highestHeartRate: 121,
      lowestHeartRate: 73,
      averageHeartRate: 94,
      hrData: makeHeartRateData("2026-05-02T07:40:00+09:00", [
        73, 80, 87, 96, 110, 121, 117, 103, 94, 86, 82, 78,
      ]),
    },
    discomfortInfo: [
      { id: 10, activityId: 6, when: "before", score: 6 },
      { id: 11, activityId: 6, when: "after", score: 2 },
    ],
    cloudInfo: [],
    coreInfo: [
      {
        index: 0,
        detailData: {
          choices: [
            { title: "몸의 감각 관찰", isUse: true, rate: 5 },
            { title: "현재 위치 말하기", isUse: true, rate: 4 },
            { title: "주변 소리 찾기", isUse: true, rate: 4 },
          ],
          selectedChoices: [
            { title: "몸의 감각 관찰", isUse: true, rate: 5 },
            { title: "현재 위치 말하기", isUse: true, rate: 4 },
          ],
        },
      },
      {
        index: 1,
        detailData: {
          choices: [
            { title: "손바닥 압박", isUse: true, rate: 3 },
            { title: "발바닥 감각 느끼기", isUse: true, rate: 5 },
          ],
          selectedChoices: [{ title: "발바닥 감각 느끼기", isUse: true, rate: 5 }],
        },
      },
    ],
    energyInfo: [],
    walkInfo: [],
    feedbacks: [{ comment: "땅 다지기 활동에서 감각 기반 안정화 전략을 잘 선택했습니다.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
  "activity-007": {
    breathingList: [],
    heartRateInfo: {
      highestHeartRate: 108,
      lowestHeartRate: 76,
      averageHeartRate: 89,
      hrData: makeHeartRateData("2026-05-04T11:00:00+09:00", [
        76, 80, 83, 87, 94, 101, 108, 99, 91, 86, 82, 79,
      ]),
    },
    discomfortInfo: [
      { id: 12, activityId: 7, when: "before", score: 4 },
      { id: 13, activityId: 7, when: "after", score: 2 },
    ],
    cloudInfo: [],
    coreInfo: [],
    energyInfo: [],
    walkInfo: [],
    feedbacks: [{ comment: "시선 활동 데이터 4종이 모두 수집된 샘플입니다.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
  "activity-008": {
    breathingList: [
      {
        startTime: "2026-05-04T22:01:00+09:00",
        endTime: "2026-05-04T22:02:00+09:00",
      },
      {
        startTime: "2026-05-04T22:05:00+09:00",
        endTime: "2026-05-04T22:06:00+09:00",
      },
    ],
    heartRateInfo: {
      highestHeartRate: 116,
      lowestHeartRate: 68,
      averageHeartRate: 85,
      hrData: makeHeartRateData("2026-05-04T22:00:00+09:00", [
        96, 110, 116, 101, 90, 84, 78, 72, 68,
      ]),
    },
    discomfortInfo: [
      { id: 14, activityId: 8, when: "before", score: 7 },
      { id: 15, activityId: 8, when: "after", score: 2 },
    ],
    cloudInfo: [],
    coreInfo: [],
    energyInfo: [],
    walkInfo: [],
    feedbacks: [{ comment: "심호흡 후 불편감 점수가 크게 감소했습니다.", createdAt: "2026-05-01T10:00:00+09:00" }],
  },
};

const eyeActivityData: Record<string, EyeActivityDataModel> = {
  "activity-002": {
    result1: [
      { time: "2026-05-02T18:12:00+09:00", x: -0.8, y: 0.1 },
      { time: "2026-05-02T18:12:01+09:00", x: -0.2, y: 0.2 },
      { time: "2026-05-02T18:12:02+09:00", x: 0.4, y: 0.1 },
      { time: "2026-05-02T18:12:03+09:00", x: 0.8, y: 0 },
    ],
    result2: [
      { time: "2026-05-02T18:13:00+09:00", x: 0, y: -0.7 },
      { time: "2026-05-02T18:13:01+09:00", x: 0.1, y: -0.1 },
      { time: "2026-05-02T18:13:02+09:00", x: 0, y: 0.5 },
    ],
    result3: [],
    result4: [],
  },
  "activity-004": {
    result1: [
      { time: "2026-05-25T08:12:00+09:00", x: -0.4, y: 0.2 },
      { time: "2026-05-25T08:12:01+09:00", x: 0.2, y: 0.1 },
      { time: "2026-05-25T08:12:02+09:00", x: 0.6, y: 0.2 },
    ],
    result2: [],
    result3: [],
    result4: [],
  },
  "activity-007": {
    result1: [
      { time: "2026-05-04T11:03:00+09:00", x: -0.9, y: 0.05 },
      { time: "2026-05-04T11:03:01+09:00", x: -0.4, y: 0.08 },
      { time: "2026-05-04T11:03:02+09:00", x: 0.1, y: 0.03 },
      { time: "2026-05-04T11:03:03+09:00", x: 0.6, y: 0.06 },
      { time: "2026-05-04T11:03:04+09:00", x: 0.9, y: 0.02 },
    ],
    result2: [
      { time: "2026-05-04T11:06:00+09:00", x: 0.02, y: -0.9 },
      { time: "2026-05-04T11:06:01+09:00", x: -0.02, y: -0.4 },
      { time: "2026-05-04T11:06:02+09:00", x: 0.01, y: 0.1 },
      { time: "2026-05-04T11:06:03+09:00", x: 0.03, y: 0.6 },
      { time: "2026-05-04T11:06:04+09:00", x: 0.01, y: 0.9 },
    ],
    result3: [
      { time: "2026-05-04T11:09:00+09:00", x: 0.8, y: -0.1 },
      { time: "2026-05-04T11:09:01+09:00", x: 0.3, y: -0.05 },
      { time: "2026-05-04T11:09:02+09:00", x: -0.2, y: -0.02 },
      { time: "2026-05-04T11:09:03+09:00", x: -0.7, y: -0.08 },
    ],
    result4: [
      { time: "2026-05-04T11:12:00+09:00", x: -0.3, y: -0.7 },
      { time: "2026-05-04T11:12:01+09:00", x: 0.2, y: -0.2 },
      { time: "2026-05-04T11:12:02+09:00", x: 0.5, y: 0.2 },
      { time: "2026-05-04T11:12:03+09:00", x: 0.1, y: 0.7 },
    ],
  },
};

const emptyEyeActivityData: EyeActivityDataModel = {
  result1: [],
  result2: [],
  result3: [],
  result4: [],
};

const doctors: MedicalExpert[] = [
  {
    role: UserRole.Doctor,
    uuid: "doctor-001",
    name: "김하늘",
    email: "doctor.mock@salpha.test",
    phone: "01012345678",
    countryCode: "82",
    activeState: "activate",
    loginAt: "2026-05-27 07:00",
  },
  {
    role: UserRole.Doctor,
    uuid: "doctor-002",
    name: "이서준",
    email: "doctor2.mock@salpha.test",
    phone: "01023456789",
    countryCode: "82",
    activeState: "activate",
    loginAt: "2026-05-26 09:00",
  },
  {
    role: UserRole.Nurse,
    uuid: "nurse-001",
    name: "박민지",
    email: "nurse.mock@salpha.test",
    phone: "01034567890",
    countryCode: "82",
    activeState: "activate",
    loginAt: "2026-05-27 08:30",
  },
  {
    role: UserRole.Cro,
    uuid: "cro-001",
    name: "최도윤",
    email: "cro.mock@salpha.test",
    phone: "01045678901",
    countryCode: "82",
    activeState: "deactivate",
    loginAt: "2026-05-20 09:00",
  },
];

const hospitals: HospitalInfoEntity[] = [
  { name: "세브란스병원", code: "H001", doctorRegisterCount: 3, patientRegisterCount: 24 },
  { name: "서울대학교병원", code: "H002", doctorRegisterCount: 2, patientRegisterCount: 11 },
];

const logs: OperationLog[] = [
  {
    uuid: "log-001",
    createdAt: "2026-05-26 09:12",
    actor: "SAT-014 관리자",
    action: "LOGIN",
    target: "admin@salphadtx.com",
    detail: "Mock 로그인",
  },
  {
    uuid: "log-002",
    createdAt: "2026-05-26 09:18",
    actor: "김하늘",
    action: "VIEW_PATIENT",
    target: "SCR-014-001",
    detail: "환자 상세 조회",
  },
  {
    uuid: "log-003",
    createdAt: "2026-05-26 09:22",
    actor: "박민지",
    action: "SEND_NOTIFICATION",
    target: "patient-002",
    detail: "복약/활동 알림 전송",
  },
];

function getSessionFromStorage(): MockAccount | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(MOCK_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as MockAccount;
  } catch {
    return null;
  }
}

function setSessionToStorage(account: MockAccount | null): void {
  if (typeof localStorage === "undefined") return;
  if (!account) {
    localStorage.removeItem(MOCK_SESSION_KEY);
    return;
  }
  localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(account));
}

function accountByLoginId(loginId: string): MockAccount {
  const normalized = loginId.trim().toLowerCase();
  const found = defaultAccounts.find((account) => account.loginId === normalized);
  if (found) return found;

  if (normalized.includes("patient")) {
    return { loginId: normalized, name: "목업 환자", role: UserRole.Patient, isExpiredSn: false };
  }
  if (normalized.includes("nurse") || normalized.includes("nuse")) {
    return {
      loginId: normalized,
      name: "목업 간호사",
      role: UserRole.Nurse,
      isExpiredSn: false,
      hospitalName: "서울대병원",
      hospitalCode: "SNU",
    };
  }
  if (normalized.includes("cro")) {
    return { loginId: normalized, name: "목업 CRO", role: UserRole.Cro, isExpiredSn: false };
  }
  if (normalized.includes("doctor") || normalized.includes("medical") || normalized.includes("hcp")) {
    return {
      loginId: normalized,
      name: "목업 의료진",
      role: UserRole.Doctor,
      isExpiredSn: false,
      hospitalName: "서울대병원",
      hospitalCode: "SNU",
    };
  }
  if (normalized.includes("super")) {
    return {
      loginId: normalized,
      name: "목업 관리자",
      role: UserRole.SuperAdmin,
      isExpiredSn: false,
    };
  }
  if (normalized.includes("admin")) {
    return {
      loginId: normalized,
      name: "목업 관리자",
      role: UserRole.SuperAdmin,
      isExpiredSn: false,
    };
  }
  return { loginId: normalized, name: "목업 관리자", role: UserRole.SuperAdmin, isExpiredSn: false };
}

export const mockStore = {
  login(loginId: string): MockAccount {
    const account = accountByLoginId(loginId);
    setSessionToStorage(account);
    return account;
  },

  logout(): void {
    setSessionToStorage(null);
  },

  currentUser(): UserInfoEntity | null {
    const account = getSessionFromStorage();
    if (!account) return null;
    return {
      email: account.loginId,
      name: account.name,
      role: account.role,
      hospitalName: account.hospitalName ?? "",
      hospitalCode: account.hospitalCode ?? "",
      isExpiredSn: account.isExpiredSn,
    };
  },

  dashboard(scope: DashboardHomeScope = "doctor"): DashboardData {
    const isAdminDashboard = scope === "admin";
    const lowCompliancePatientCount = patients.filter((patient) => isLowCompliancePatient(patient.uuid)).length;
    return {
      cards: [
        {
          metric: "totalPatients",
          title: "전체 환자 수",
          value: patients.length,
          percentageChange: 12,
        },
        {
          metric: "inProgressPatients",
          title: "진행중인 환자 수",
          value: patients.filter((patient) => isInProgressStatus(patient.progressStatus)).length,
          percentageChange: -8,
        },
        {
          metric: "lowCompliance",
          title: "순응도 60% 미만",
          value: lowCompliancePatientCount,
          percentageChange: 0,
        },
      ],
      rows: Object.values(prescriptionsByPatient)
        .flat()
        .map((prescription) => {
          const patient = patients.find((item) => item.uuid === prescription.patientUuid);
          const patientIndex = patient ? patients.findIndex((item) => item.uuid === patient.uuid) : -1;
          return {
            doctorName: patient?.doctorName ?? null,
            userName: isAdminDashboard ? patient?.screeningId ?? "-" : patient?.patientName ?? "-",
            patientId: patientIndex >= 0 ? patientIndex + 1 : "-",
            screeningId: patient?.screeningId ?? "-",
            prescriptionNumber: prescription.prescriptionId ?? prescription.prescriptionUuid,
            status: prescription.prescriptionStatus ?? "-",
            progressPercentage: prescription.complianceRate ?? 0,
            prescriptionType: prescription.prescriptionType ?? "-",
            patientUuid: prescription.patientUuid,
            prescriptionUuid: prescription.prescriptionUuid,
            treatments: (prescriptionsByPatient[prescription.patientUuid] ?? []).map((item) => ({
              treatmentUuid: item.prescriptionUuid,
              treatmentId: item.prescriptionId,
              status: item.prescriptionStatus,
              startDate: item.startDate ?? null,
              endDate: item.endDate ?? null,
              type: item.prescriptionType,
              complianceRate: item.complianceRate ?? null,
            })),
          };
        }),
    };
  },

  patientSummaries(filter?: PatientListFilter): PatientSummary[] {
    return patients.filter((patient) => matchesPatientFilter(patient, filter)).map(patientSummary);
  },

  patient(uuid: string): PatientDetail | null {
    const patient = patients.find((item) => item.uuid === uuid);
    return patient ? { ...patient, latestComplianceRate: latestComplianceRate(patient.uuid) } : null;
  },

  prescriptionList(patientUuid: string): PrescriptionSummary[] {
    return (prescriptionsByPatient[patientUuid] ?? []).map((prescription) => ({ ...prescription }));
  },

  prescription(patientUuid: string, prescriptionUuid: string): PrescriptionDetail | null {
    const prescription = (prescriptionsByPatient[patientUuid] ?? []).find(
      (item) => item.prescriptionUuid === prescriptionUuid,
    );
    return prescription ? { ...prescription } : null;
  },

  activityDetail(activityUuid: string): ActivityDetail | null {
    const detail = activityDetails[activityUuid];
    return detail
      ? {
          ...detail,
          breathingList: detail.breathingList.map((item) => ({ ...item })),
          discomfortInfo: detail.discomfortInfo.map((item) => ({ ...item })),
          heartRateInfo: {
            ...detail.heartRateInfo,
            hrData: detail.heartRateInfo.hrData.map((item) => ({ ...item })),
          },
          cloudInfo: detail.cloudInfo.map((item) => ({ ...item })),
          coreInfo: detail.coreInfo.map((item) => ({ ...item })),
          energyInfo: detail.energyInfo.map((item) => ({ ...item })),
          walkInfo: detail.walkInfo.map((item) => ({ ...item })),
        }
      : null;
  },

  eyeActivityData(activityUuid: string): EyeActivityDataModel {
    const data = eyeActivityData[activityUuid] ?? emptyEyeActivityData;
    return {
      result1: data.result1.map((item) => ({ ...item })),
      result2: data.result2.map((item) => ({ ...item })),
      result3: data.result3.map((item) => ({ ...item })),
      result4: data.result4.map((item) => ({ ...item })),
    };
  },

  updateActivityFeedback(activityUuid: string, comment: string): void {
    if (activityDetails[activityUuid]) {
      activityDetails[activityUuid] = {
        ...activityDetails[activityUuid],
        feedbacks: [
          ...(activityDetails[activityUuid].feedbacks ?? []),
          { comment, createdAt: new Date().toISOString() },
        ],
      };
    }
  },

  doctors(): MedicalExpert[] {
    return doctors.map((doctor) => ({ ...doctor }));
  },

  doctor(uuid: string): MedicalExpert | null {
    const doctor = doctors.find((item) => item.uuid === uuid);
    return doctor ? { ...doctor } : null;
  },

  addDoctor(payload: {
    fullName?: string;
    name?: string;
    email?: string;
    phone?: string;
    hospitalCode?: string;
    memo?: string;
  }): void {
    doctors.push({
      role: UserRole.Doctor,
      uuid: `doctor-${Date.now()}`,
      name: String(payload.fullName ?? payload.name ?? "신규 의사"),
      email: String(payload.email ?? "new@example.com"),
      phone: String(payload.phone ?? "-"),
      countryCode: "KR",
      activeState: "active",
      loginAt: null,
      hospitalCode: payload.hospitalCode ? String(payload.hospitalCode) : null,
      memo: payload.memo ? String(payload.memo) : null,
    });
  },

  logs(page: number, pageSize: number) {
    const start = (page - 1) * pageSize;
    return {
      totalPage: Math.max(1, Math.ceil(logs.length / pageSize)),
      totalCount: logs.length,
      data: logs.slice(start, start + pageSize).map((log) => ({ ...log })),
    };
  },

  hospitals(): HospitalInfoEntity[] {
    return hospitals.map((hospital) => ({ ...hospital }));
  },

  hospital(code: string): HospitalInfoEntity | null {
    const hospital = hospitals.find((item) => item.code === code);
    return hospital ? { ...hospital } : null;
  },

  addHospital(hospital: HospitalInfoEntity): void {
    hospitals.push(hospital);
  },

  deleteHospital(code: string): void {
    const index = hospitals.findIndex((item) => item.code === code);
    if (index >= 0) hospitals.splice(index, 1);
  },

  addTreatment(payload: AddTreatmentRequestPayload): string {
    const newTreatmentUuid = `treatment-${Date.now()}`;
    const newPrescription: PrescriptionDetail = {
      patientUuid: payload.patientUuid,
      prescriptionUuid: newTreatmentUuid,
      prescriptionId: `RX-014-${String(Date.now()).slice(-4)}`,
      prescriptionStatus: "inProgress",
      prescriptionType: String(payload.type),
      startDate: payload.startDate,
      endDate: payload.endDate,
      complianceRate: 0,
      memo: "목업 치료 생성",
      plans: [],
      activities: [],
    };
    prescriptionsByPatient[payload.patientUuid] = [
      ...(prescriptionsByPatient[payload.patientUuid] ?? []),
      newPrescription,
    ];
    return newTreatmentUuid;
  },
};
